import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, ChevronDown, FileText, Image, FileDown, Folder, FolderOpen } from 'lucide-react';
import SystemHeader from '../SystemHeader';

// Detect file type from extension
const getFileType = (file) => {
  if (!file) return 'folder';
  const ext = file.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  return 'markdown';
};

const FILE_ICONS = {
  markdown: { Icon: FileText, color: '#3b82f6', bg: '#eff6ff' },
  image: { Icon: Image, color: '#10b981', bg: '#ecfdf5' },
  pdf: { Icon: FileDown, color: '#ef4444', bg: '#fef2f2' },
  folder: { Icon: Folder, color: '#f59e0b', bg: '#fffbeb' },
};

// ─── Tree Item ──────────────────────────
const TreeItem = ({ item, depth = 0, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (item.enabled === false) return null;

  const isFolder = item.type === 'folder';
  const fileType = isFolder ? 'folder' : getFileType(item.file);
  const iconCfg = FILE_ICONS[fileType] || FILE_ICONS.markdown;
  const FolderIcon = expanded ? FolderOpen : Folder;
  const ItemIcon = isFolder ? FolderIcon : iconCfg.Icon;

  const children = (item.children || [])
    .filter(c => c.enabled !== false)
    .sort((a, b) => (a.order || 99) - (b.order || 99));

  const handleClick = () => {
    if (isFolder) {
      setExpanded(!expanded);
    } else {
      // Navigate to detail with file path encoded
      navigate(`/kien-thuc/${item.id}`, { state: { item } });
    }
  };

  return (
    <div>
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors border-b border-gray-100"
        style={{ paddingLeft: `${16 + depth * 20}px` }}
        onClick={handleClick}
      >
        {/* Folder expand indicator */}
        {isFolder ? (
          <div className="w-5 flex-shrink-0 flex items-center justify-center">
            {expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
          </div>
        ) : (
          <div className="w-5 flex-shrink-0" />
        )}

        {/* Icon */}
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: isFolder ? (expanded ? '#fef3c7' : '#fffbeb') : iconCfg.bg }}>
          <ItemIcon size={18} style={{ color: isFolder ? '#d97706' : iconCfg.color }} />
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-semibold ${isFolder ? 'text-gray-800' : 'text-gray-700'}`}>{item.label}</h3>
          {item.desc && <p className="text-xs text-gray-400 mt-0.5 truncate">{item.desc}</p>}
        </div>

        {/* Badge for folders */}
        {isFolder && children.length > 0 && (
          <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium flex-shrink-0">
            {children.length}
          </span>
        )}

        {/* Arrow for files */}
        {!isFolder && <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />}
      </div>

      {/* Children */}
      {isFolder && expanded && (
        <div className="bg-gray-50/50">
          {children.length === 0 ? (
            <div className="text-xs text-gray-400 py-3 text-center" style={{ paddingLeft: `${36 + depth * 20}px` }}>
              Chưa có nội dung
            </div>
          ) : (
            children.map((child) => (
              <TreeItem key={child.id} item={child} depth={depth + 1} navigate={navigate} />
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main List ──────────────────────────
const KienThucList = ({ userInfo, onLogout, totalPoints, onRefreshPoints }) => {
  const navigate = useNavigate();
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await fetch(`/kienthuc/kienthuc.config.json?v=${Date.now()}`);
        if (res.ok) {
          const data = await res.json();
          setTree((data.tree || []).sort((a, b) => (a.order || 99) - (b.order || 99)));
        } else {
          // Fallback: try src config
          const { default: cfg } = await import('../../config/kienthucConfig.json');
          setTree((cfg.tree || []).sort((a, b) => (a.order || 99) - (b.order || 99)));
        }
      } catch (e) {
        console.warn('Failed to load kienthuc config:', e);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SystemHeader userInfo={userInfo} onLogout={onLogout} totalPoints={totalPoints} pageTitle="Kiến thức" onRefreshPoints={onRefreshPoints} />

      <div className="flex-1 max-w-2xl w-full mx-auto flex flex-col">
        {/* Top bar */}
        <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
          <button onClick={() => navigate('/dashboard')} className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-base font-semibold text-gray-900">📚 Kiến thức</h1>
        </div>

        {/* Tree view */}
        <div className="flex-1 overflow-y-auto bg-white">
          {loading ? (
            <div className="text-center py-12 text-gray-500 text-sm">Đang tải...</div>
          ) : tree.filter(t => t.enabled !== false).length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">Chưa có nội dung kiến thức</div>
          ) : (
            tree
              .filter(t => t.enabled !== false)
              .map((item) => (
                <TreeItem key={item.id} item={item} depth={0} navigate={navigate} />
              ))
          )}
        </div>
      </div>
    </div>
  );
};

export default KienThucList;
